/**
 * CortexTerminal — Terminal brief + interactive chat for Cortex.
 *
 * Types out a summary of Cortex's purpose, value, and innovation,
 * then transitions to a guided chat with three starter questions.
 * Uses Cortex-specific RAG via /api/cortex-chat.
 */
import { useState, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useChat } from '../../hooks/useChat';

// ─── Terminal Configuration ──────────────────────────────

type LineStyle = 'command' | 'system' | 'status' | 'text' | 'header' | 'feature' | 'detail' | 'diagram' | 'highlight' | 'blank';

interface TermLine {
  text: string;
  style: LineStyle;
  charMs?: number;
  pauseMs?: number;
}

const CHAR_SPEED: Record<LineStyle, number> = {
  command: 28,
  system: 14,
  status: 10,
  text: 18,
  header: 0,
  feature: 16,
  detail: 14,
  diagram: 0,
  highlight: 24,
  blank: 0,
};

const LINE_PAUSE: Record<LineStyle, number> = {
  command: 400,
  system: 150,
  status: 80,
  text: 100,
  header: 200,
  feature: 100,
  detail: 50,
  diagram: 60,
  highlight: 300,
  blank: 60,
};

// ─── Terminal Content ────────────────────────────────────

const LINES: TermLine[] = [
  { text: '$ cortex --about', style: 'command' },
  { text: '', style: 'blank' },
  { text: 'cortex v1.0.0', style: 'system' },
  { text: 'loading intelligence layer...', style: 'system', pauseMs: 500 },
  { text: '', style: 'blank' },
  { text: '  memory engine    ··· ONLINE', style: 'status' },
  { text: '  anomaly detector ··· 7 TYPES', style: 'status' },
  { text: '  anti-patterns    ··· 3 DETECTORS', style: 'status' },
  { text: '  session bridge   ··· CONNECTED', style: 'status' },
  { text: '', style: 'blank' },
  { text: '── WHAT IS CORTEX ─────────────────────', style: 'header' },
  { text: '', style: 'blank' },
  { text: 'The missing layer between AI sessions.', style: 'text' },
  { text: 'LLM agents are powerful but amnesiac —', style: 'text' },
  { text: 'every session starts from zero.', style: 'text' },
  { text: 'Cortex gives them persistent memory.', style: 'text' },
  { text: '', style: 'blank' },
  { text: '  session₁ ──→ remember ──→ session₂', style: 'diagram' },
  { text: '    ╰──── learn ──→ recommend ──╯', style: 'diagram' },
  { text: '', style: 'blank' },
  { text: '▸ MEMORY · three-tier persistent storage', style: 'feature' },
  { text: '  Short-term context. Working decisions.', style: 'detail' },
  { text: '  Long-term patterns. Nothing lost.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: '▸ ANOMALY · automated detection', style: 'feature' },
  { text: '  7 detector types catch metric spikes,', style: 'detail' },
  { text: '  test regressions, and service failures', style: 'detail' },
  { text: '  before they reach production.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: '▸ INTELLIGENCE · cross-project learning', style: 'feature' },
  { text: '  Query your entire codebase history.', style: 'detail' },
  { text: '  Pattern matches surface what worked.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: '▸ OUTCOMES · compounding improvement', style: 'feature' },
  { text: '  Every result feeds back. Confidence', style: 'detail' },
  { text: '  scores update. The system gets smarter.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: 'open source · apache 2.0 · pip install cortex-intelligence', style: 'highlight' },
  { text: '', style: 'blank' },
];

// ─── Starter Questions ──────────────────────────────────

const STARTER_QUESTIONS = [
  'How does three-tier memory work?',
  'What anomaly types does Cortex detect?',
  'How is this different from RAG?',
];

// ─── Line Rendering ─────────────────────────────────────

const BASE_COLORS: Record<LineStyle, string> = {
  command: 'text-cyan-400',
  system: 'text-gray-500',
  status: 'text-gray-400',
  text: 'text-gray-400',
  header: 'text-gray-600',
  feature: 'text-cyan-300',
  detail: 'text-gray-500',
  diagram: 'text-gray-600',
  highlight: 'text-white',
  blank: '',
};

function renderCompletedLine(line: TermLine): ReactElement {
  const { text, style } = line;

  if (style === 'blank') return <>{'\u00A0'}</>;

  // Status: color the status word
  if (style === 'status') {
    const parts = text.split('···');
    if (parts.length === 2) {
      const word = parts[1].trim();
      const color = word === 'ONLINE' || word === 'CONNECTED'
        ? 'text-green-400'
        : word === '7 TYPES' || word === '3 DETECTORS'
        ? 'text-amber-400'
        : 'text-cyan-400';
      return (
        <>
          <span className="text-gray-600">{parts[0]}··· </span>
          <span className={color}>{word}</span>
        </>
      );
    }
  }

  // Command: $ in gray, rest in cyan
  if (style === 'command') {
    if (text === '$') return <span className="text-gray-600">$</span>;
    return (
      <>
        <span className="text-gray-600">$ </span>
        <span className="text-cyan-400">{text.slice(2)}</span>
      </>
    );
  }

  // Feature: ▸ NAME · description
  if (style === 'feature') {
    const match = text.match(/^(▸ )(\w+)( · )(.+)$/);
    if (match) {
      return (
        <>
          <span className="text-gray-600">{match[1]}</span>
          <span className="text-cyan-400">{match[2]}</span>
          <span className="text-gray-700">{match[3]}</span>
          <span className="text-gray-300">{match[4]}</span>
        </>
      );
    }
  }

  // Highlight: pip install line
  if (style === 'highlight') {
    const parts = text.split('·').map(s => s.trim());
    return (
      <>
        {parts.map((part, i) => (
          <span key={i}>
            {i > 0 && <span className="text-gray-700"> · </span>}
            {part.startsWith('pip') ? (
              <span className="text-cyan-400">{part}</span>
            ) : (
              <span className="text-white">{part}</span>
            )}
          </span>
        ))}
        <span className="text-cyan-400"> ◆</span>
      </>
    );
  }

  return <>{text}</>;
}

// ─── Follow-up Parser ────────────────────────────────────
// Splits "?> question" lines from the response text so they
// can be rendered as clickable suggestion buttons.

function parseFollowUps(content: string): { text: string; questions: string[] } {
  const lines = content.split('\n');
  const questions: string[] = [];
  const textLines: string[] = [];

  for (const line of lines) {
    if (line.trim().startsWith('?> ')) {
      questions.push(line.trim().slice(3));
    } else {
      textLines.push(line);
    }
  }

  return {
    text: textLines.join('\n').trimEnd(),
    questions,
  };
}

// ─── Component ───────────────────────────────────────────

export const CortexTerminal = () => {
  const termBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [completedLines, setCompletedLines] = useState(1);
  const [currentChars, setCurrentChars] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [typingDone, setTypingDone] = useState(false);
  const [chatInput, setChatInput] = useState('');

  const timerRef = useRef<number>(0);

  // Chat hook — Cortex-specific endpoint
  const { messages, isLoading, streamingContent, sendMessage, clearChat } = useChat('/api/cortex-chat');
  const hasMessages = messages.length > 0 || !!streamingContent;

  // Export conversation
  const exportToMarkdown = useCallback(() => {
    if (messages.length === 0) return;
    const date = new Date().toISOString().slice(0, 10);
    const lines = [
      '# Cortex — Conversation',
      `*Exported ${new Date().toLocaleString()}*`,
      '', '---', '',
    ];
    for (const msg of messages) {
      if (msg.role === 'user') {
        lines.push(`**You:** ${msg.content}`, '');
      } else {
        lines.push(msg.content, '');
      }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cortex-chat-${date}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [messages]);

  // Cursor blink
  useEffect(() => {
    const id = setInterval(() => setShowCursor(v => !v), 530);
    return () => clearInterval(id);
  }, []);

  // Focus input when typing finishes
  useEffect(() => {
    if (typingDone && inputRef.current) {
      inputRef.current.focus();
    }
  }, [typingDone]);

  // Auto-scroll terminal
  useEffect(() => {
    if (termBodyRef.current) {
      termBodyRef.current.scrollTop = termBodyRef.current.scrollHeight;
    }
  }, [messages, streamingContent, completedLines, currentChars]);

  // Typing engine
  useEffect(() => {
    let lineIdx = 1;
    let charIdx = 0;
    let cancelled = false;

    const advance = () => {
      if (cancelled || lineIdx >= LINES.length) {
        setTypingDone(true);
        return;
      }

      const line = LINES[lineIdx];
      const speed = line.charMs ?? CHAR_SPEED[line.style];
      const pause = line.pauseMs ?? LINE_PAUSE[line.style];

      if (speed === 0 || line.text.length === 0) {
        lineIdx++;
        setCompletedLines(lineIdx);
        setCurrentChars(0);
        charIdx = 0;
        timerRef.current = window.setTimeout(advance, pause);
        return;
      }

      charIdx++;
      setCurrentChars(charIdx);

      if (charIdx >= line.text.length) {
        lineIdx++;
        setCompletedLines(lineIdx);
        setCurrentChars(0);
        charIdx = 0;
        timerRef.current = window.setTimeout(advance, pause);
      } else {
        timerRef.current = window.setTimeout(advance, speed);
      }
    };

    timerRef.current = window.setTimeout(advance, 600);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, []);

  const handleSend = (text: string) => {
    if (!text.trim() || isLoading) return;
    sendMessage(text);
    setChatInput('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend(chatInput);
  };

  const currentLine = LINES[completedLines];
  const isTyping = currentLine && currentChars > 0;

  const cursor = (
    <span
      className={`inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px ${
        showCursor ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ transition: 'opacity 0.1s' }}
    />
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Link to="/" className="text-sm font-mono text-gray-500 hover:text-gray-300 transition-colors">
            &larr; Kempion
          </Link>
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-green-500"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-xs font-mono text-accent/60 uppercase tracking-widest">Cortex</span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <div className="w-full max-w-3xl">
          {/* Terminal Window */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="rounded-xl border border-gray-800/80 overflow-hidden shadow-2xl shadow-cyan-900/10"
          >
            {/* Title Bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 border-b border-gray-800/50">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500/60" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <span className="w-3 h-3 rounded-full bg-green-500/60" />
              </div>
              <span className="text-[11px] text-gray-600 font-mono ml-2">
                cortex — intelligence
              </span>

              {hasMessages && (
                <div className="ml-auto flex items-center gap-3">
                  <button
                    onClick={exportToMarkdown}
                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                    title="Export conversation as markdown"
                  >
                    export .md
                  </button>
                  <button
                    onClick={clearChat}
                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                  >
                    clear
                  </button>
                </div>
              )}
            </div>

            {/* Terminal Body */}
            <div
              ref={termBodyRef}
              className="bg-[#080808] px-5 py-5 md:px-6 md:py-6 font-mono text-[12px] md:text-[13px] leading-[1.7] min-h-[420px] max-h-[600px] overflow-y-auto overflow-x-auto"
              role="log"
              aria-label="Cortex terminal output"
            >
              {/* Completed lines */}
              {LINES.slice(0, completedLines).map((line, i) => (
                <div key={i} className={`${BASE_COLORS[line.style]} whitespace-pre`}>
                  {renderCompletedLine(line)}
                </div>
              ))}

              {/* Currently typing line */}
              {isTyping && (
                <div className={`${BASE_COLORS[currentLine.style]} whitespace-pre`}>
                  {currentLine.text.slice(0, currentChars)}
                  {cursor}
                </div>
              )}

              {/* Post-typing: Chat Interface */}
              {typingDone && !isTyping && (
                <>
                  {/* Starter questions */}
                  {!hasMessages && (
                    <div className="mt-1">
                      <div className="text-gray-600 whitespace-pre">{'── explore cortex ─────────────────────'}</div>
                      <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                      {STARTER_QUESTIONS.map((q) => (
                        <button
                          key={q}
                          onClick={() => handleSend(q)}
                          className="group block w-full text-left py-1 text-gray-400 hover:text-cyan-400 transition-all duration-150 cursor-pointer"
                        >
                          <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
                          <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{q}</span>
                        </button>
                      ))}
                      <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                    </div>
                  )}

                  {/* Chat messages */}
                  {messages.map((msg, msgIdx) => {
                    if (msg.role === 'user') {
                      return (
                        <div key={msg.id} className="mt-1">
                          <div className="whitespace-pre">
                            <span className="text-gray-600">$ </span>
                            <span className="text-cyan-400">{msg.content}</span>
                          </div>
                        </div>
                      );
                    }

                    const { text, questions } = parseFollowUps(msg.content);
                    const isLastAssistant = msgIdx === messages.length - 1;

                    return (
                      <div key={msg.id} className="mt-1">
                        <div className="whitespace-pre-wrap text-gray-400 pl-2">
                          {text}
                        </div>
                        {isLastAssistant && questions.length > 0 && !isLoading && (
                          <div className="mt-2 pl-2">
                            <div className="text-gray-700 text-[11px] font-mono mb-1">follow up:</div>
                            {questions.map((q) => (
                              <button
                                key={q}
                                onClick={() => handleSend(q)}
                                className="group block w-full text-left py-0.5 text-gray-500 hover:text-cyan-400 transition-all duration-150 cursor-pointer"
                              >
                                <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
                                <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{q}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Streaming response */}
                  {streamingContent && (
                    <div className="mt-1 whitespace-pre-wrap text-gray-400 pl-2">
                      {streamingContent}
                      <span className="inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px animate-pulse" />
                    </div>
                  )}

                  {/* Input prompt */}
                  <form onSubmit={handleSubmit} className="flex items-center mt-1">
                    <span className="text-gray-600">$ </span>
                    {!isLoading && !chatInput && cursor}
                    <input
                      ref={inputRef}
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={isLoading ? '' : 'ask about cortex...'}
                      disabled={isLoading}
                      className="flex-1 bg-transparent text-cyan-400 placeholder:text-gray-700 outline-none disabled:opacity-50 ml-1 caret-cyan-400"
                    />
                    {isLoading && (
                      <span className="flex items-center gap-1 ml-2">
                        <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                        <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                        <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce" />
                      </span>
                    )}
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[150px] opacity-30" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px] opacity-20" />
      </div>
    </div>
  );
};
